import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { buildUserPasswordPayload, buildUserSavePayload, mapUserFromApi, GENDER_OPTIONS } from './userUtils';
import '../css/Category.css';

const UserCreate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    Username: '',
    Password: '',
    ConfirmPassword: '',
    MobileNumber: '',
    Gender: '',
    DateOfBirth: '',
    RoleId: '',
    IsEmailVerified: false,
    IsMobileVerified: false,
    IsBlocked: false,
  });
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    apiFetch('/User/GetRoleDropdown')
      .then(({ data }) => {
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setRoles(list);
      })
      .catch((e) => { console.error('Role dropdown error:', e.message); setRoles([]); });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!form.FirstName.trim()) { setFormError('First Name is required.'); return; }
    if (!form.LastName.trim()) { setFormError('Last Name is required.'); return; }
    if (!form.Email.trim()) { setFormError('Email is required.'); return; }
    if (!form.Username.trim()) { setFormError('Username is required.'); return; }
    if (!form.Password.trim()) { setFormError('Password is required.'); return; }
    if (form.Password !== form.ConfirmPassword) { setFormError('Passwords do not match.'); return; }

    setSaving(true);
    try {
      await apiFetch('/User/Create', {
        method: 'POST',
        data: {
          ...buildUserSavePayload(form),
          ...buildUserPasswordPayload(form.Password),
        },
      });
      navigate('/admin/user', { state: { toast: 'User created successfully.' } });
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <AdminLink to="/admin/user">Users</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Add User</span>
      </p>

      <div className="row">
        <div className="col-lg-12 col-md-10 col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-1">Add User</h4>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>Basic user information</p>

              {formError && (
                <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                  <i className="mdi mdi-alert-circle me-1"></i>{formError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">First Name <span className="text-danger">*</span></label>
                      <input className="form-control" name="FirstName" value={form.FirstName} onChange={handleChange} placeholder="First Name" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Last Name <span className="text-danger">*</span></label>
                      <input className="form-control" name="LastName" value={form.LastName} onChange={handleChange} placeholder="Last Name" />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Email address <span className="text-danger">*</span></label>
                      <input className="form-control" type="email" name="Email" value={form.Email} onChange={handleChange} placeholder="Email" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Username <span className="text-danger">*</span></label>
                      <input className="form-control" name="Username" value={form.Username} onChange={handleChange} placeholder="Username" />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Password <span className="text-danger">*</span></label>
                      <input className="form-control" type="password" name="Password" value={form.Password} onChange={handleChange} placeholder="Password" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Confirm Password <span className="text-danger">*</span></label>
                      <input className="form-control" type="password" name="ConfirmPassword" value={form.ConfirmPassword} onChange={handleChange} placeholder="Confirm Password" />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Mobile Number</label>
                      <input className="form-control" type="tel" name="MobileNumber" value={form.MobileNumber} onChange={handleChange} placeholder="Mobile Number" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Gender</label>
                      <select className="form-control" name="Gender" value={form.Gender} onChange={handleChange}>
                        <option value="">— Select Gender —</option>
                        {GENDER_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Date of Birth</label>
                      <input className="form-control" type="date" name="DateOfBirth" value={form.DateOfBirth} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">Role</label>
                      <select className="form-control" name="RoleId" value={form.RoleId} onChange={handleChange}>
                        <option value="">— Select Role —</option>
                        {roles.map(r => (
                          <option key={r.RoleId} value={r.RoleId}>{r.Name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="IsEmailVerified"
                    id="isEmailVerified"
                    checked={form.IsEmailVerified}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="isEmailVerified">Email Verified</label>
                </div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="IsMobileVerified"
                    id="isMobileVerified"
                    checked={form.IsMobileVerified}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="isMobileVerified">Mobile Verified</label>
                </div>

                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="IsBlocked"
                    id="isBlocked"
                    checked={form.IsBlocked}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="isBlocked">Blocked</label>
                </div>

                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? (
                      <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving...</>
                    ) : 'Submit'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/user')}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserCreate;
